// Dependencies
import { useTranslation } from 'react-i18next';
import axios, { CancelTokenSource } from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect } from 'react';
// Core & Lab
import { CircularProgress, FormControl, FormHelperText } from '@mui/material';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
// Actions
import { updateDestination } from './redux/actions';
// Types
import { AppState } from '../../reducers';
import { DestinationReduced } from '../destination/objects/destinationReduced';
import { Circuit } from './objects/circuit';
import { updateCircuitInfoErrors } from './redux/reducer';

type Props = {
    circuit: Circuit | null
}

let request_timeout: NodeJS.Timeout;
let cancelToken: CancelTokenSource;

export function CircuitDestination(props: Props): JSX.Element {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState<DestinationReduced[]>([]);
    const quotationCode = useSelector((state: AppState) => state.header.quotation_code);
    const destinationValue = useSelector((state: AppState) => state.circuit.destination);
    const client_id = useSelector((state: AppState) => state.header.client_id);
    const circuit_info_errors = useSelector((state: AppState) => state.circuitSlice.circuit_info_errors);

    const onSearchChange = (event: React.SyntheticEvent<Element, Event>, value: string) => {
        setSearch(value);
    };

    const runRequest = () => {
        setLoading(true);
        if (typeof cancelToken != typeof undefined) {
            cancelToken.cancel("Operation canceled due to new request.");
        }
        cancelToken = axios.CancelToken.source();
        axios({
            method: 'GET',
            url: `${API_HREF}client/${client_id}/destinations/flash_search/`,
            headers: {
                'Authorization': `Token ${localStorage.getItem('token')}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Accept-Language': i18n.language
            },
            cancelToken: cancelToken.token,
            params: {
                reduced: true,
                visibility__in: "PUBLIC,PRIVATE_TO",
                current_version__type__in: quotationCode === 'verdie' ?
                    '1,2' :
                    null,
                search,
                ordering: 'current_version__type',
            }
        }).then((response) => {
            setOptions(response.data.map((destination: any) => ({ id: destination.destination_id, data: { international_name: destination.international_name, name: destination.name } })));
            setLoading(false);
        }).catch((error) => {
            if (!axios.isCancel(error)) {
                console.log('error : ', error);
                setLoading(false);
            }
        });
    };

    useEffect(() => {
        clearTimeout(request_timeout);
        if (search.trim().length !== 0) {
            request_timeout = setTimeout(runRequest, 500);
        } else {
            setOptions([]);
        }
    }, [search]);

    // recreate input
    useEffect(() => {
        if (props.circuit?.destination?.data) {
            const destination = {
                ...props.circuit.destination,
                data: {
                    ...props.circuit.destination.data,
                    localization: props.circuit.destination.data.localization,
                    bounds: props.circuit.destination.data.bounds ?? '',
                    currency: null
                }
            };
            dispatch(updateDestination(destination));
        }
    }, [props.circuit]);

    return (
        <FormControl fullWidth error={!!circuit_info_errors.destination}>
            <Autocomplete
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.data?.name || option.data?.international_name || option.international_name}
                options={options}
                loading={loading}
                noOptionsText={ t('list.autocomplete_no_options_text') }
                loadingText={ t('list.autocomplete_loading_text') }
                value={destinationValue}
                filterOptions={(options) => options}
                onInputChange={onSearchChange}
                onChange={(event, value) => {
                    // @TODO fix type :'(
                    dispatch(updateDestination(value as any));
                    dispatch(updateCircuitInfoErrors({ ...circuit_info_errors, destination: '' }));
                }}
                renderInput={(params) => (
                    <TextField
                        required
                        {...params}
                        label={t('shared.destination')}
                        error={!!circuit_info_errors.destination}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color={'inherit'} size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
            <FormHelperText>{circuit_info_errors.destination}</FormHelperText>
        </FormControl>
    );
}